import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ModalController } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { NewsModalComponent } from '../components/news-modal/news-modal.component';

export interface ReleaseEntry {
  version: string;
  publishedAt: string;
  badge: string;
  title: string;
  summary: string;
  details: string;
  content?: string;
}

@Injectable({ providedIn: 'root' })
export class NewsService {
  private readonly http = inject(HttpClient);
  private readonly modalCtrl = inject(ModalController);

  async checkAndShowIfNeeded(): Promise<void> {
    try {
      const [releases, lastSeenNewsAt] = await Promise.all([
        this.loadLatestRelease(),
        this.loadLastSeenNewsAt(),
      ]);

      if (!releases) return;

      const latestPublishedAt = new Date(releases.publishedAt).getTime();
      const lastSeen = lastSeenNewsAt ? new Date(lastSeenNewsAt).getTime() : 0;

      if (latestPublishedAt <= lastSeen) return;

      await this.showNewsModal(releases);
    } catch {
      // Non-critical — never block app startup
    }
  }

  async markAsRead(publishedAt: string): Promise<void> {
    try {
      await firstValueFrom(
        this.http.put(
          `${environment.apiurl}/users/me/preferences`,
          { lastSeenNewsAt: new Date(publishedAt).toISOString() },
        ),
      );
    } catch {
      // Best-effort — user sees modal again next time, that's fine
    }
  }

  private async loadLatestRelease(): Promise<ReleaseEntry | null> {
    try {
      const data = await firstValueFrom(
        this.http.get<{ releases: ReleaseEntry[] }>(`assets/release-news.json?_t=${Date.now()}`),
      );
      const sorted = (data?.releases ?? []).sort(
        (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime(),
      );
      return sorted[0] ?? null;
    } catch {
      return null;
    }
  }

  private async loadLastSeenNewsAt(): Promise<string | null> {
    try {
      const prefs = await firstValueFrom(
        this.http.get<{ lastSeenNewsAt?: string | null }>(`${environment.apiurl}/users/me/preferences`),
      );
      return prefs?.lastSeenNewsAt ?? null;
    } catch {
      return null;
    }
  }

  private async showNewsModal(release: ReleaseEntry): Promise<void> {
    const modal = await this.modalCtrl.create({
      component: NewsModalComponent,
      componentProps: {
        data: {
          badge: release.badge,
          title: release.title,
          publishedAt: release.publishedAt,
          content: release.content ?? null,
          details: release.details ?? '',
        },
        isAutoShow: true,
      },
      cssClass: 'news-modal-wrapper',
    });

    await modal.present();

    const result = await modal.onDidDismiss<{ read: boolean }>();
    if (result.data?.read) {
      await this.markAsRead(release.publishedAt);
    }
  }
}
